import CallMergeIcon from "@mui/icons-material/CallMerge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, Divider, Grid, Paper, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCustomersByIds } from "../../../../services/customersService";

import { setsEqual } from "../../../../utils";
import { CustomersDetailsTable } from "./components/CustomersDetailsTable";
import { EditSharesList } from "./components/EditSharesList";
import { MergeSharesDialog } from "./components/MergeSharesDialog";

export function CustomersDataTab(props) {
    const { control, fields, readOnly = false } = props;
    const { t } = useTranslation();
    const { setValue } = useFormContext();

    const selectedCustomerIdsRaw = useWatch({ control, name: fields.customerIds });
    const selectedCustomerIds = useMemo(
        () => selectedCustomerIdsRaw ?? [],
        [selectedCustomerIdsRaw],
    );
    const sharesRaw = useWatch({ control, name: fields.customerShares });
    const shares = useMemo(() => sharesRaw ?? [], [sharesRaw]);

    const flagsRaw = useWatch({ control, name: fields.customerFlags });
    const flags = useMemo(() => flagsRaw ?? {}, [flagsRaw]);

    const handleSetFlags = useCallback(
        (customerId, customerFlags) => {
            if (readOnly) {
                return;
            }
            const nextFlags = { ...flags, [customerId]: customerFlags };
            setValue(fields.customerFlags, nextFlags);
        },
        [fields.customerFlags, flags, readOnly, setValue],
    );

    // keep shares in sync with selected customers, this will add/remove shares when we select/deselect customers
    useEffect(() => {
        const setSelected = new Set(selectedCustomerIds);
        const nextShares = shares
            .map((s) => ({
                customerIds: s.customerIds.filter((id) => setSelected.has(id)),
                percentage: s.percentage,
            }))
            .filter((s) => s.customerIds.length > 0);

        let changed = nextShares.length !== shares.length;

        selectedCustomerIds.forEach((id) => {
            const inAny = nextShares.some((s) => s.customerIds.includes(id));
            if (!inAny) {
                nextShares.push({ customerIds: [id], percentage: 0 });
                changed = true;
            }
        });

        if (changed) {
            setValue(fields.customerShares, nextShares);
        }
    }, [selectedCustomerIds, shares, setValue, fields.customerShares]);

    // Keep flags in sync with selected customers, adding false flags for new customers
    useEffect(() => {
        const customerIdsInFlags = new Set(Object.keys(flags));
        const selectedCustomerIdsSet = new Set(selectedCustomerIds);
        if (setsEqual(customerIdsInFlags, selectedCustomerIdsSet)) {
            return;
        }

        const nextFlags = Object.fromEntries(
            selectedCustomerIds.map((cid) => [
                cid,
                flags[cid] || { isForeigner: false, isJdg: false, isNip: false },
            ]),
        );
        setValue(fields.customerFlags, nextFlags);
    }, [selectedCustomerIds, flags, setValue, fields.customerFlags]);

    const { data: customers = [] } = useCustomersByIds(selectedCustomerIds, {
        enabled: selectedCustomerIds.length > 0,
    });

    useEffect(() => {
        if (readOnly || selectedCustomerIds.length === 0) {
            return;
        }

        const hasNonZeroShares = shares.some((s) => s.percentage > 0);
        if (hasNonZeroShares) {
            return;
        }

        const equalShare = Math.floor(100 / selectedCustomerIds.length);
        const remainder = 100 - equalShare * selectedCustomerIds.length;

        const newShares = selectedCustomerIds.map((id, index) => ({
            customerIds: [id],
            percentage: index === 0 ? equalShare + remainder : equalShare,
        }));

        setValue(fields.customerShares, newShares);
    }, []);

    const updateShares = (next) => {
        if (readOnly) {
            return;
        }
        setValue(fields.customerShares, next);
    };

    const splitShares = () => {
        if (readOnly) {
            return;
        }
        const out = [];
        for (const s of shares) {
            if (s.customerIds.length <= 1) {
                out.push(s);
            } else {
                const per = s.percentage / s.customerIds.length;
                s.customerIds.forEach((id) => out.push({ customerIds: [id], percentage: per }));
            }
        }
        updateShares(out);
    };

    // merge dialog
    const [mergeOpen, setMergeOpen] = useState(false);
    const handleConfirmMerge = (mergeSelectedIds) => {
        if (readOnly || mergeSelectedIds.length < 2) {
            setMergeOpen(false);
            return;
        }
        const idsToMerge = new Set(mergeSelectedIds);

        let mergedPercentage = 0;
        const remaining = [];
        for (const s of shares) {
            const keepIds = s.customerIds.filter((id) => !idsToMerge.has(id));
            const removedCount = s.customerIds.length - keepIds.length;
            if (removedCount === 0) {
                remaining.push(s);
            } else {
                const removedPart = (s.percentage * removedCount) / s.customerIds.length;
                mergedPercentage += removedPart;
                if (keepIds.length > 0) {
                    const keptPart = s.percentage - removedPart;
                    remaining.push({ customerIds: keepIds, percentage: keptPart });
                }
            }
        }
        remaining.push({ customerIds: mergeSelectedIds, percentage: mergedPercentage });
        updateShares(remaining);
        setMergeOpen(false);
    };

    const sharesAllGTZero = shares.every((s) => s.percentage > 0);
    const sharesSum = shares.reduce(
        (acc, s) => acc + (Number.isFinite(s.percentage) ? s.percentage : 0),
        0,
    );
    const sharesSumIs100 = Math.abs(sharesSum - 100) < 0.0001;

    return (
        <Grid container direction="column" spacing={4}>
            <Grid item>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {t("agreements.customersData.sharesTitle") || "Shares"}
                    </Typography>
                    <Box>
                        <Button
                            size="small"
                            startIcon={<CallMergeIcon />}
                            onClick={() => setMergeOpen(true)}
                            disabled={readOnly || selectedCustomerIds.length < 2}
                            sx={{ mr: 1 }}
                        >
                            {t("agreements.customersData.mergeShares") || "Merge shares"}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<CallSplitIcon />}
                            onClick={splitShares}
                            disabled={readOnly || shares.every((s) => s.customerIds.length <= 1)}
                        >
                            {t("agreements.customersData.splitShares") || "Split shares"}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <Grid item>
                <Grid container spacing={3} alignItems="flex-start">
                    {/* shares list */}
                    <Grid item xs={12} md={9}>
                        <EditSharesList
                            shares={shares}
                            customers={customers}
                            readOnly={readOnly}
                            onChange={updateShares}
                        />
                    </Grid>

                    {/* indicators container */}
                    <Grid item xs={12} md={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t("agreements.customersData.sharesValidationTitle") ||
                                    "Shares validation"}
                            </Typography>

                            <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip
                                    title={t("agreements.customersData.sharesGreaterThanZero")}
                                >
                                    {sharesAllGTZero ? (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                        <HighlightOffIcon color="error" fontSize="small" />
                                    )}
                                </Tooltip>
                                <Typography variant="body2">
                                    {t("agreements.customersData.checkGreaterThanZero")}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                                <Tooltip title={t("agreements.customersData.sharesSumTo100")}>
                                    {sharesSumIs100 ? (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                        <HighlightOffIcon color="error" fontSize="small" />
                                    )}
                                </Tooltip>
                                <Typography variant="body2">
                                    {t("agreements.customersData.checkSumTo100")}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Divider sx={{ my: 3 }} />
            </Grid>
            <Grid item>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                    {t("agreements.customersData.detailsTitle")}
                </Typography>
                <CustomersDetailsTable
                    customers={customers}
                    flags={flags}
                    setCustomerFlags={handleSetFlags}
                />
            </Grid>
            <MergeSharesDialog
                open={mergeOpen}
                onClose={() => setMergeOpen(false)}
                selectedCustomerIds={selectedCustomerIds}
                customers={customers}
                onConfirm={handleConfirmMerge}
            />
        </Grid>
    );
}
